import { useEffect, useMemo, useState } from 'preact/hooks';
import { formatRoomCode } from './SessionHeader';

export interface ShareInviteProps {
  readonly inviteUrl: string;
  readonly roomCode: string;
  readonly pin: string;
}

export function ShareInvite({ inviteUrl, roomCode, pin }: ShareInviteProps) {
  const [status, setStatus] = useState<string | null>(null);
  const social = useMemo(() => socialShareLinks(inviteUrl, roomCode), [inviteUrl, roomCode]);

  useEffect(() => {
    if (status === null) return;
    const timeout = window.setTimeout(() => setStatus(null), 2_500);
    return () => window.clearTimeout(timeout);
  }, [status]);

  const shareInvite = async () => {
    const data = {
      title: 'Join my screen share',
      text: `Join room ${formatRoomCode(roomCode)} with PIN ${pin}.`,
      url: inviteUrl,
    };
    try {
      if (typeof navigator.share === 'function' && navigator.canShare?.(data) !== false) {
        await navigator.share(data);
        setStatus('Invite shared.');
      } else {
        await navigator.clipboard.writeText(inviteUrl);
        setStatus('Private invite link copied.');
      }
    } catch (cause) {
      if (!(cause instanceof DOMException && cause.name === 'AbortError')) {
        setStatus('The invite could not be shared. Try a social option instead.');
      }
    }
  };

  return (
    <div class="share-invite">
      <div class="share-invite__heading">
        <span>Share screen invite</span>
        <span aria-hidden="true" />
      </div>
      <div class="share-invite__actions">
        <button class="native-share" type="button" onClick={() => void shareInvite()}>
          <ShareIcon />
          Share invite
        </button>
        {social.map(({ label, href, icon }) => (
          <a
            class="social-share"
            key={label}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Share via ${label}`}
            title={label}
          >
            <SocialIcon name={icon} />
          </a>
        ))}
      </div>
      <p>
        Social buttons share only the room link. Send the PIN separately to keep access private.
      </p>
      <span class="sr-only" role="status" aria-live="polite">
        {status ?? ''}
      </span>
    </div>
  );
}

export function socialShareLinks(inviteUrl: string, roomCode: string) {
  let publicUrl: URL;
  try {
    publicUrl = new URL(inviteUrl);
  } catch {
    return [];
  }
  if (publicUrl.protocol !== 'https:' && publicUrl.protocol !== 'http:') return [];
  publicUrl.hash = '';
  const url = encodeURIComponent(publicUrl.toString());
  const text = encodeURIComponent(
    `Join my browser screen share in room ${formatRoomCode(roomCode)}. Ask me for the PIN.`,
  );
  return [
    { label: 'WhatsApp', icon: 'whatsapp', href: `https://wa.me/?text=${text}%20${url}` },
    { label: 'X', icon: 'x', href: `https://twitter.com/intent/tweet?text=${text}&url=${url}` },
    {
      label: 'Facebook',
      icon: 'facebook',
      href: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
    },
    {
      label: 'LinkedIn',
      icon: 'linkedin',
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
    },
    {
      label: 'Email',
      icon: 'email',
      href: `mailto:?subject=${encodeURIComponent('Join my screen share')}&body=${text}%0A%0A${url}`,
    },
  ] as const;
}

function ShareIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="18" cy="5" r="2.5" />
      <circle cx="6" cy="12" r="2.5" />
      <circle cx="18" cy="19" r="2.5" />
      <path d="m8.2 10.8 7.6-4.5m-7.6 6.9 7.6 4.5" />
    </svg>
  );
}

function SocialIcon({
  name,
}: {
  readonly name: 'whatsapp' | 'x' | 'facebook' | 'linkedin' | 'email';
}) {
  if (name === 'x') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M5 4.5 18.7 20M19 4.5 5.3 20" />
      </svg>
    );
  }
  if (name === 'facebook') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M14.5 21v-8h2.8l.5-3h-3.3V8.1c0-.9.3-1.6 1.7-1.6H18V3.8c-.3 0-1.3-.1-2.5-.1-2.5 0-4.2 1.5-4.2 4.3v2H8.5v3h2.8v8" />
      </svg>
    );
  }
  if (name === 'linkedin') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M6 9v11M6 5.5v.01M10.5 20v-6.2c0-2.4 3-3.3 4.7-1.5.5.6.8 1.4.8 2.4V20M10.5 9v11" />
      </svg>
    );
  }
  if (name === 'email') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <path d="m4 7 8 6 8-6" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M20 11.6a8 8 0 0 1-11.8 7L4 20l1.4-4A8 8 0 1 1 20 11.6Z" />
      <path d="M9 8.2c.3 2.8 2 4.5 4.8 4.8" />
    </svg>
  );
}
